// components/PriceCalculator.tsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  Grid,
  InputAdornment,
  Paper,
  TextField,
  Typography
} from '@mui/material';
import {
  AttachMoney as MoneyIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';

type PriceCalculatorProps = {
  incAmount: number;
  onIncAmountChange: (amount: number) => void;
  showCalculator: boolean;
  onToggleCalculator: () => void;
  get: (url: string, auth?: boolean) => Promise<any>; // pass in your useNetwork().get
};

export const PriceCalculator: React.FC<PriceCalculatorProps> = ({
  incAmount,
  onIncAmountChange,
  showCalculator,
  onToggleCalculator,
  get
}) => {
  const [usdPrice, setUsdPrice] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [incTokenPrice, setIncTokenPrice] = useState<number>(0);

  const fetchTokenPrice = async () => {
    setLoading(true);
    try {
      const data = await get('/token/price', true);
      setIncTokenPrice(data.price);
      setUsdPrice(incAmount * data.price);
    } catch (error) {
      console.error('Error fetching token price:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (showCalculator) fetchTokenPrice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCalculator]);

  useEffect(() => {
    if (incTokenPrice > 0) setUsdPrice(incAmount * incTokenPrice);
  }, [incAmount, incTokenPrice]);

  return (
    <Box sx={{ mb: 2 }}>
      <Button
        variant="outlined"
        onClick={onToggleCalculator}
        startIcon={<MoneyIcon />}
        endIcon={showCalculator ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        sx={{
          mb: 2,
          borderColor: '#38e8ff',
          color: '#38e8ff',
          '&:hover': {
            borderColor: '#22d3ee',
            backgroundColor: 'rgba(56, 232, 255, 0.1)'
          }
        }}
      >
        Price Calculator
      </Button>

      <Collapse in={showCalculator}>
        <Paper
          sx={{
            p: 2,
            backgroundColor: 'rgba(56, 232, 255, 0.1)',
            border: '1px solid #38e8ff'
          }}
        >
          <Typography variant="h6" gutterBottom sx={{ color: '#38e8ff' }}>
            INC ↔ USD Price Calculator
          </Typography>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <TextField
                label="INC Amount"
                type="number"
                value={incAmount}
                onChange={(e) =>
                  onIncAmountChange(parseFloat(e.target.value) || 0)
                }
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">INC</InputAdornment>
                  )
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="USD Value"
                value={loading ? 'Calculating...' : `$${usdPrice.toFixed(2)}`}
                fullWidth
                disabled
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  )
                }}
              />
            </Grid>
          </Grid>

          {incTokenPrice > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              1 INC = ${incTokenPrice.toFixed(6)} USD
            </Typography>
          )}
        </Paper>
      </Collapse>
    </Box>
  );
};
